import { useAnnouncementRichTextEditor } from '@components/announcement/hooks/useAnnouncementRichTextEditor.hooks';
import {
  fromAnnouncementRichTextContentDTO,
  toAnnouncementRichTextContentDTO,
} from '@components/announcement/util/announcementRichTextContent.util';
import { type AnnouncementItemDTO } from '@model/announcement/AnnouncementItemDTO';
import { type JSONContent } from '@tiptap/core';
import { type Editor } from '@tiptap/react';
import { useMemo, useState } from 'react';

const EMPTY_CONTENT: JSONContent = { type: 'doc', content: [] };

interface UseAnnouncementContentProps {
  existingItem: AnnouncementItemDTO | undefined;
  fontNameById: Map<number, string>;
  fontIdByName: Map<string, number>;
  editable: boolean;
  onDirty: () => void;
}

interface UseAnnouncementContentResult {
  content: JSONContent;
  editor: Editor | null;
  richTextContent: ReturnType<typeof toAnnouncementRichTextContentDTO>;
}

// Owns the rich text editor's content in both directions: loaded once from
// the item's DTO (by font name, since that's what tiptap mark attrs use),
// edited via the tiptap editor instance, and re-derived back to the DTO
// shape (by font id, since that's what the backend expects) whenever it
// changes.
export const useAnnouncementContent = ({
  existingItem,
  fontNameById,
  fontIdByName,
  editable,
  onDirty,
}: UseAnnouncementContentProps): UseAnnouncementContentResult => {
  const [content, setContent] = useState<JSONContent>(() =>
    existingItem?.richTextContent
      ? fromAnnouncementRichTextContentDTO(existingItem.richTextContent, fontNameById)
      : EMPTY_CONTENT,
  );

  const handleContentChange = (value: JSONContent) => {
    setContent(value);
    onDirty();
  };

  const editor = useAnnouncementRichTextEditor({
    value: content,
    editable,
    onChange: handleContentChange,
  });

  const richTextContent = useMemo(
    () => toAnnouncementRichTextContentDTO(content, fontIdByName),
    [content, fontIdByName],
  );

  return { content, editor, richTextContent };
};
